import { useEffect, useMemo, useRef, useState } from "react";
import BaseCard from "../../components/ui/BaseCard";
import { useQuery } from "@tanstack/react-query";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import { Chip } from "@mui/material";
import { fetchCompanies, fetchCompanyUsers } from "../../services/api/company";
import { debounce } from "../../utils/helpers";
import { useNavigate, useParams } from "react-router-dom";
import { UserType } from "../../utils/enum";
import dayjs from "dayjs";
import ApproveEngineerModal from "./components/ApproveEngineerModal";
import SendEmailToEngineerModal from "./components/SendEmailToEngineerModal";
import AdminFilters from "./AdminFilters";
import { useDispatch, useSelector } from "react-redux";
import { setAdminFilters } from "../../store/filters/adminFiltersSlice";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import { deleteUser } from "../../services/api/user";
import { showToast } from "../../store/toast/toastSlice";
import BaseWrapper from "../../components/ui/BaseWrapper";
const menuOptions = [
  // {
  //   label: "Delete",
  //   value: "DELETE",
  //   icon: <i className="fa-solid fa-toggle-off"></i>,
  // },
];
const ClientUsersTable = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isApproveEngineerModalOpen, setIsApproveEngineerModalOpen] = useState(false);
  const [isSendEmailToEngineerModalOpen, setIsSendEmailToEngineerModalOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const companyUsersFilters = useSelector((s) => s.adminFilters.companyUsers);
  const { page, pageSize } = companyUsersFilters.pagination;
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  const filtersPayload = useMemo(
    () => ({
      fullName: companyUsersFilters.fullName || undefined,
      email: companyUsersFilters.email || undefined,
      country: companyUsersFilters.country?.label || undefined,
      role: companyUsersFilters.role || undefined,
      status: companyUsersFilters.status || undefined,
    }),
    [companyUsersFilters],
  );

  const baseQueryParams = {
    role: UserType.FREELANCER,
    page: page + 1,
    limit: pageSize,
    search: "",
    id: id,
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);
  const [paginationModel, setPaginationModel] = useState({
    page,
    pageSize,
  });

  const [newUsersList, setNewUsersList] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const [selectedEngineer, setSelectedEngineer] = useState(null);

  const {
    data: companyUsers,
    isPending: isCompanyUsersApiLoading,
    refetch: refetchCompanyUsers,
  } = useQuery({
    queryKey: [
      "companyUsers",
      queryParams.limit,
      queryParams.page,
      queryParams.search,
      queryParams.id,
      filtersPayload,
    ],
    queryFn: () => fetchCompanyUsers({ ...queryParams, ...filtersPayload }),
  });
  const { data: companies, isPending } = useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanies,
  });

  useEffect(() => {
    if (companyUsers?.data) {
      setNewUsersList(companyUsers.data.users);
      setRowCount(companyUsers.data.totalCount);
    }
  }, [companyUsers]);

  const applyUserStatusChange = (id, status) => {
    setNewUsersList((prev) =>
      Array.isArray(prev)
        ? prev.map((user) => (String(user.id) === String(id) ? { ...user, status } : user))
        : prev,
    );
    refetchCompanyUsers();
  };

  const renderHeaderWithIcon = (title) => (
    <div className="flex items-center gap-2">
      <span className="text-[#525354] font-medium">{title}</span>
    </div>
  );

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.7,
      minWidth: 80,
      renderHeader: () => renderHeaderWithIcon("ID"),
      renderCell: (params) => <p className="cursor-pointer">{params?.value}</p>,
    },
    {
      field: "firstName",
      headerName: "Full Name",
      flex: 0.7,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Full Name"),
      renderCell: (params) => (
        <span className="text-primary cursor-pointer">
          {params.row.firstName} {params.row.lastName}
        </span>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.9,
      minWidth: 220,
      renderHeader: () => renderHeaderWithIcon("Email"),
      renderCell: (params) => <p>{params.value}</p>,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.5,
      minWidth: 200,
      renderHeader: () => renderHeaderWithIcon("Address"),
      renderCell: (params) => <p>{params.row?.personalAddress?.addressLine1 || "-"}</p>,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.7,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Country"),
      renderCell: (params) => <p>{params.row?.personalAddress?.country || "-"}</p>,
    },
    {
      field: "Role",
      headerName: "Role",
      flex: 0.7,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Role"),
      renderCell: (params) => <p>{params?.row?.designation ? params?.row?.designation : "-"}</p>,
    },
    {
      field: "isVerified",
      headerName: "Status",
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) =>
        params.row.status === "APPROVED" ? (
          <Chip label="Active" color="primary" size="small" className="w-24 justify-center" />
        ) : params.row.status === "DEACTIVATED" ? (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        ) : (
          <Chip
            label="Invited"
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.7,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Created At"),
      renderCell: (params) => <p>{dayjs(params.value).format("YYYY/MM/DD hh:mm A")}</p>,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 90,
      renderHeader: () => renderHeaderWithIcon("Actions"),
      renderCell: (params) => {
        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params?.row)}
          />
        );
      },
    },
  ];
  const handleItemClick = async (item, data) => {
    setSelectedUser(data);
    if (item === "DELETE") {
      setDeleteReason("");
      setIsConfirmDeleteOpen(true);
    }
  };
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (!deleteReason?.trim()) {
      dispatch(
        showToast({
          message: "Please provide a reason before deleting the user.",
          type: "error",
        }),
      );
      return;
    }
    try {
      setIsDeleting(true);
      await deleteUser(selectedUser.id, deleteReason);
      setIsConfirmDeleteOpen(false);
      refetchCompanyUsers();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };
  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));

    dispatch(
      setAdminFilters({
        module: "companyUsers",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "firstName") {
      navigate(`/user/details/profile/${params?.row?.id}`);
    }

    return;
  };
  const appliedFilters = useMemo(() => {
    const filters = [];

    if (companyUsersFilters.fullName)
      filters.push({ prefix: "Name", value: companyUsersFilters.fullName, key: "fullName" });

    if (companyUsersFilters.email)
      filters.push({ prefix: "Email", value: companyUsersFilters.email, key: "email" });

    if (companyUsersFilters.country?.label)
      filters.push({ prefix: "Country", value: companyUsersFilters.country.label, key: "country" });

    if (companyUsersFilters.role)
      filters.push({ prefix: "Role", value: companyUsersFilters.role, key: "role" });
    const statusLabelMap = {
      PENDING: "Pending",
      SUBMITTED: "Submitted",
      APPROVED: "Active",
      ACTIVATED: "Active",
      DEACTIVATED: "In-Active",
    };
    if (companyUsersFilters.status) {
      const displayStatus =
        statusLabelMap[companyUsersFilters.status] || companyUsersFilters.status;
      filters.push({
        prefix: "Status",
        value: displayStatus,
        key: "status",
      });
    }
    return filters;
  }, [companyUsersFilters]);
  const handleRemoveFilter = (key) => {
    if (["fullName", "email", "role", "status"].includes(key)) {
      dispatch(setAdminFilters({ module: "companyUsers", filterName: key, data: "" }));
    } else if (key === "country") {
      dispatch(
        setAdminFilters({
          module: "companyUsers",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
    }

    dispatch(
      setAdminFilters({
        module: "companyUsers",
        filterName: "pagination",
        data: { page: 0, pageSize: companyUsersFilters.pagination.pageSize },
      }),
    );

    setFiltersChanged(true);
  };

  return (
    <>
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
            <div className="md:col-span-3 col-span-7">
              <h3 className=" md:text-left text-center">Tech Domain Pty Ltd's Users</h3>
            </div>
          </div>
        </BaseWrapper>
      </div>
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex justify-between items-center w-full">
            <div
              className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => {
                  return (
                    <div
                      key={(f.key || "") + (f.value || "") + (f.id || "")}
                      className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                    >
                      <span className="text-primary">{f.prefix}:</span>&nbsp;
                      <p className="text-primary text-sm mx-1">{f.value}</p>
                      <button
                        onClick={() => handleRemoveFilter(f.key, f.id)}
                        className="ml-2 text-primary hover:text-red-500"
                        aria-label={`Remove ${f.prefix} ${f.value}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className=" w-[28%]">
              <BaseTextField
                placeholder={`Search`}
                value={searchQuery} // Directly bind to searchQuery state
                onChange={handleSearchChange}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
              />
            </div>
          </div>
        </div>

        <div className="max-h-[900px] overflow-y-auto">
          <BaseDataTableV2
            className="max-h-[calc(100vh-320px)] overflow-auto mt-4"
            rowHeight={38}
            columns={columns}
            data={newUsersList}
            isLoading={isCompanyUsersApiLoading}
            disableColumnResize={false}
            onCellClick={handleCellClick}
            hideFooter={false}
            pagination
            rowCount={rowCount}
            pageSizeOptions={[10, 20, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            paginationMode="server"
            checkboxSelection
          />
        </div>
        {isConfirmDeleteOpen && (
          <BaseConfirmModal
            open={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onConfirm={handleDeleteUser}
            isLoading={isDeleting}
            title="Are you sure you want to delete this user?"
            description={
              <>
                <BaseTextField
                  label="Reason"
                  name="reason"
                  placeholder="Reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  labelShrink={false}
                  optional={false}
                  isClearable
                  isDefaultField={false}
                />
                <p className="mt-2 ">This action cannot be undone.</p>
              </>
            }
            confirmText="Delete"
            confirmButtonColor="error"
          />
        )}
        {isApproveEngineerModalOpen && (
          <ApproveEngineerModal
            isApproveEngineerModalOpen={isApproveEngineerModalOpen}
            setIsApproveEngineerModalOpen={setIsApproveEngineerModalOpen}
            onStatusChange={applyUserStatusChange}
            selectedEngineer={selectedEngineer}
          />
        )}

        {isSendEmailToEngineerModalOpen && (
          <SendEmailToEngineerModal
            setIsSendEmailToEngineerModalOpen={setIsSendEmailToEngineerModalOpen}
            isSendEmailToEngineerModalOpen={isSendEmailToEngineerModalOpen}
            onStatusChange={applyUserStatusChange}
            selectedEngineer={selectedEngineer}
          />
        )}
        {isFilterDrawerOpen && (
          <AdminFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchData={() => refetchCompanyUsers()}
            isLoading={isCompanyUsersApiLoading}
            // setFiltersChanged={setFiltersChanged}
            entity="companyUsers"
          />
        )}
      </BaseWrapper>
    </>
  );
};

export default ClientUsersTable;
